import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPeople, fetchTotalPeopleCount, fetchPersonBooks, BookWithRole } from "@/api/people";
import { useSessionState } from "@/hooks/useSessionState";
import { getAllRolesWithCounts } from "@/api/peopleRoles";
import { Person } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Search,
  ArrowUpDown,
  Plus,
  BookOpen,
  Mic,
  Languages,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePagination } from "@/hooks/usePagination";


// Colors for role badges - match the role indicator icon colors
const getRoleBadgeColor = (roleName: string): string => {
  switch (roleName.toLowerCase()) {
    case "author":
      return "bg-blue-500/10 text-blue-600";
    case "narrator":
      return "bg-green-500/10 text-green-600";
    case "translator":
      return "bg-amber-500/10 text-amber-600";
    default:
      return "bg-primary/10 text-primary";
  }
};

const PeopleList = () => {
  const navigate = useNavigate();

  // State for People
  const [rawSearchQuery, setRawSearchQuery] = useSessionState("peopleList.rawSearchQuery", "");
  const [peopleSearchQuery, setPeopleSearchQuery] = useSessionState("peopleList.peopleSearchQuery", "");
  const [peopleRoleFilter, setPeopleRoleFilter] = useSessionState("peopleList.peopleRoleFilter", "");
  const [currentPage, setCurrentPage] = useSessionState("peopleList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("peopleList.pageSize", 10); // Display 10 people per page
  const [peopleSortConfig, setPeopleSortConfig] = useSessionState<{
    key: keyof Person | "roleNames";
    direction: "asc" | "desc";
  }>("peopleList.peopleSortConfig", {
    key: "name",
    direction: "asc",
  });

  // Debounce the search input to prevent frequent API calls while typing
  const debouncedSearchTerm = useDebounce(rawSearchQuery, 500);

  // Update the actual search query when debounced search term changes
  useEffect(() => {
    setPeopleSearchQuery(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  // Fetch people with pagination
  const { data: peopleData, isLoading: isLoadingPeople } = useQuery({
    queryKey: [
      "people",
      peopleSearchQuery,
      peopleRoleFilter,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      fetchPeople(currentPage, pageSize, {
        search: peopleSearchQuery,
        roleId:
          peopleRoleFilter === "all" || !peopleRoleFilter
            ? undefined
            : Number(peopleRoleFilter),
      }),
  });

  // Fetch total people count regardless of filters
  const { data: totalPeopleCount } = useQuery({
    queryKey: ["totalPeople"],
    queryFn: fetchTotalPeopleCount,
  });

  // Fetch roles with counts in a single query
  const { data: rolesWithCounts = [], isLoading: isLoadingRoleCounts } =
    useQuery({
      queryKey: ["peopleRoleCounts"],
      queryFn: getAllRolesWithCounts,
    });

  // State for person books modal
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch person's books when modal is opened
  const { data: personBooks, isLoading: isLoadingPersonBooks } = useQuery({
    queryKey: ["personBooks", selectedPersonId],
    queryFn: () => {
      if (!selectedPersonId) return Promise.resolve([]);
      return fetchPersonBooks(selectedPersonId);
    },
    enabled: !!selectedPersonId && isModalOpen,
  });

  // Handle opening modal
  const handleOpenBooksModal = (personId: number, personName: string) => {
    setSelectedPersonId(personId);
    setSelectedPersonName(personName);
    setIsModalOpen(true);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPersonId(null);
    setSelectedPersonName("");
  };

  // Render role indicators for a book (used in modal)
  const renderRoleIndicators = (book: BookWithRole) => {
    return (
      <div className="flex gap-1 items-center ml-1">
        {book.roleTypes.isAuthor && (
          <span className="tooltip" aria-label="Author">
            <BookOpen className="h-3 w-3 text-blue-600" />
          </span>
        )}
        {book.roleTypes.isNarrator && (
          <span className="tooltip" aria-label="Narrator">
            <Mic className="h-3 w-3 text-green-600" />
          </span>
        )}
        {book.roleTypes.isTranslator && (
          <span className="tooltip" aria-label="Translator">
            <Languages className="h-3 w-3 text-amber-600" />
          </span>
        )}
      </div>
    );
  };

  // Handle sorting for people
  const handlePeopleSort = (column: keyof Person | "roleNames") => {
    setPeopleSortConfig((prev) => ({
      key: column,
      direction:
        prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Sort roles by people count for display in stats
  const sortedRolesWithCounts = [...rolesWithCounts].sort(
    (a, b) => (b.peopleCount || 0) - (a.peopleCount || 0)
  );

  // Filter and sort people
  const filteredAndSortedPeople = peopleData?.data
    ? [...peopleData.data].sort((a, b) => {
        const direction = peopleSortConfig.direction === "asc" ? 1 : -1;

        if (peopleSortConfig.key === "roleNames") {
          const rolesA = a.roles.map((r) => r.name).join(", ");
          const rolesB = b.roles.map((r) => r.name).join(", ");
          return direction * rolesA.localeCompare(rolesB);
        }

        if (
          typeof a[peopleSortConfig.key] === "string" &&
          typeof b[peopleSortConfig.key] === "string"
        ) {
          return (
            direction *
            (a[peopleSortConfig.key] as string).localeCompare(
              b[peopleSortConfig.key] as string
            )
          );
        }

        return 0;
      })
    : [];

  const handleAddPerson = () => {
    navigate("/admin/people/new");
  };

  // Reset function for people filters - also reset pagination
  const resetPeopleFilters = () => {
    setRawSearchQuery("");
    setPeopleSearchQuery("");
    setPeopleRoleFilter("");
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: peopleData?.total || 0,
    itemsPerPage: pageSize,
    currentPage,
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const isLoading = isLoadingPeople || isLoadingRoleCounts;
  const totalPeople = totalPeopleCount || 0;

  return (
    <AdminLayout
      title="People"
      actionButton={
        <Button onClick={handleAddPerson} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Person
        </Button>
      }
    >
      {/* Stats Section removed for performance */}

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Search and filter people</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search people..."
                className="pl-10"
                value={rawSearchQuery}
                onChange={(e) => {
                  setRawSearchQuery(e.target.value);
                }}
              />
            </div>
            <div className="w-full sm:w-[180px]">
              <Select
                value={peopleRoleFilter || "all"}
                onValueChange={(value) => {
                  // Set to empty string for "all" to avoid filtering
                  setPeopleRoleFilter(value === "all" ? "" : value);
                  setCurrentPage(1); // Reset to first page when filtering
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {rolesWithCounts.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={resetPeopleFilters}
              className="ml-auto"
              disabled={!peopleSearchQuery && !peopleRoleFilter}
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="rounded-md border border-border">
          <div className="px-4 py-2 border-b border-border bg-muted/50 text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <strong>Book role indicators:</strong>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3 text-blue-600" /> Author
              </span>
              <span className="flex items-center gap-1">
                <Mic className="h-3 w-3 text-green-600" /> Narrator
              </span>
              <span className="flex items-center gap-1">
                <Languages className="h-3 w-3 text-amber-600" /> Translator
              </span>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead className="w-[300px]">Name</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handlePeopleSort("roleNames")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Roles</span>
                    {peopleSortConfig.key === "roleNames" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          peopleSortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-1">
                    <span>Associated Books</span>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedPeople.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No people found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedPeople.map((person) => (
                  <TableRow
                    key={person.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/people/${person.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={person.avatarUrl} />
                          <AvatarFallback>
                            {person.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{person.name}</div>
                          {person.email && (
                            <div className="text-sm text-muted-foreground">
                              {person.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {person.roles.map((role) => (
                          <Badge
                            key={role.id}
                            className={cn(
                              getRoleBadgeColor(role.name),
                              "font-medium px-2 py-0.5 text-xs"
                            )}
                            variant="outline"
                          >
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenBooksModal(person.id, person.name);
                        }}
                        disabled={(person.bookCount || 0) === 0}
                      >
                        <BookOpen className="h-3 w-3 mr-1" />
                        {person.bookCount || 0} {person.bookCount === 1 ? "book" : "books"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* After the people table, add this pagination UI */}
      <div className="flex items-center justify-between px-4 py-4 border-t border-border mt-4">
        <div className="text-sm text-muted-foreground">
          Showing{" "}
          <span className="font-medium">
            {peopleData && peopleData.total > 0
              ? Math.min((currentPage - 1) * pageSize + 1, peopleData.total)
              : 0}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {peopleData
              ? Math.min(currentPage * pageSize, peopleData.total)
              : 0}
          </span>{" "}
          of <span className="font-medium">{totalPeople}</span> people
        </div>
        <div className="flex items-center gap-4">
          {peopleData && totalPages > 0 && (
            <div className="flex items-center">
              <Button
                className="mr-1 px-2"
                variant="outline"
                size="sm"
                onClick={() => goToPage(1)}
                disabled={!canGoPrev}
              >
                First
              </Button>

              <Button
                className="mr-1 px-2"
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={!canGoPrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {pageNumbers.map((pageNum) => (
                <Button
                  key={pageNum}
                  className="mx-0.5 w-8 h-8 p-0"
                  variant={pageNum === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ))}

              <Button
                className="ml-1 px-2"
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!canGoNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                className="ml-1 px-2"
                variant="outline"
                size="sm"
                onClick={() => goToPage(totalPages)}
                disabled={!canGoNext}
              >
                Last
              </Button>
            </div>
          )}

          <select
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
          >
            {[10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Person Books Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Books by {selectedPersonName}</DialogTitle>
            <DialogDescription>
              {isLoadingPersonBooks
                ? "Loading books..."
                : personBooks && personBooks.length > 0
                ? `Showing ${personBooks.length} ${personBooks.length === 1 ? "book" : "books"}`
                : "No books found"}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {isLoadingPersonBooks ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : personBooks && personBooks.length > 0 ? (
              <div className="space-y-3">
                {personBooks.map((book) => (
                  <div
                    key={book.id}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm">{book.title}</h4>
                        {renderRoleIndicators(book)}
                      </div>
                      {book.authors && book.authors.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          by {book.authors.map((a) => a.name).join(", ")}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {book.personRole}
                        </Badge>
                        {book.readingTime > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {book.readingTime} min read
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No books found for this person.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PeopleList;
